import type { ToolMeta } from '../tools/registry';
import { currentClientLanguage } from './registry';

const BRAND_NAME = 'PDF911';

export type InstallCopy = {
  install: string;
  homeScreen: string;
  save: string;
  installHint: string;
  iosHint: string;
  saveHint: string;
  iosTitle: string;
  iosLead: string;
  iosSteps: string[];
  androidTitle: string;
  androidLead: string;
  androidSteps: string[];
  desktopTitle: string;
  desktopLead: string;
  desktopSteps: (shortcut: string) => string[];
};

export type UiCopy = {
  languageLabel: string;
  footer: string;
  privacyLink: string;
  dataLink: string;
  close: string;
  analyticsTitle: string;
  analyticsLead: string;
  details: string;
  accept: string;
  switchToLightTheme: string;
  switchToDarkTheme: string;
  lightTheme: string;
  darkTheme: string;
  homeHeadline: string;
  heroLead: string;
  privateTitle: string;
  privateText: string;
  privacyChip: string;
  pdfTools: string;
  toolFilterLabel: string;
  homeToolsLead: string;
  dropFile: string;
  fileTypes: string;
  chooseFile: string;
  selectedFile: string;
  countingPages: string;
  pageCountUnavailable: string;
  pageSingular: string;
  pagePlural: string;
  replaceFile: string;
  removeFile: string;
  choosePdfTool: string;
  all: string;
  popular: string;
  compressFilter: string;
  recommendedPdfTools: string;
  detectedPdf: string;
  howItWorks: string;
  howStepChoose: string;
  howStepProcess: string;
  howStepDownload: string;
  howStepSecure: string;
  homeTrustFooter: string;
  faq: string;
  commonTasks: string;
  popularSearches: string;
  morePdfTools: string;
  morePdfToolsLead: string;
  allTools: string;
  current: string;
  openingTool: string;
  loadToolError: string;
  checkConnection: string;
  retry: string;
  openingSettings: string;
  settingsLoadError: string;
  pageLoadError: string;
  categories: Record<ToolMeta['category'], string>;
  install: InstallCopy;
};

type UiCopyOverride = Partial<Omit<UiCopy, 'categories' | 'install'>> & {
  categories?: Partial<Record<ToolMeta['category'], string>>;
  install?: Partial<InstallCopy>;
};

const EN_UI_COPY: UiCopy = {
  languageLabel: 'Language',
  footer: `${BRAND_NAME} — free PDF tools for merging, compressing, splitting, editing and converting files in the browser.`,
  privacyLink: 'Privacy',
  dataLink: 'Data',
  close: 'Close',
  analyticsTitle: 'Analytics cookies',
  analyticsLead: `${BRAND_NAME} uses privacy-conscious analytics. Files are not uploaded.`,
  details: 'Details',
  accept: 'Accept',
  switchToLightTheme: 'Switch to light theme',
  switchToDarkTheme: 'Switch to dark theme',
  lightTheme: 'Light theme',
  darkTheme: 'Dark theme',
  homeHeadline: 'Merge, compress and convert PDF online',
  heroLead: 'Fast PDF tools that work right in your browser. No uploads. Your files stay private and secure.',
  privateTitle: 'Private by design',
  privateText: 'Files are processed in your browser and are not uploaded to our server. No account required.',
  privacyChip: 'Files stay on your device. Private and secure.',
  pdfTools: 'PDF tools',
  toolFilterLabel: 'Tool filter',
  homeToolsLead: 'Use PDF911 to organize pages, merge documents, compress PDFs, convert files and add quick edits without sending documents to a server.',
  dropFile: 'Drop files here or choose files',
  fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
  chooseFile: 'Choose files',
  selectedFile: 'Selected file:',
  countingPages: 'counting pages...',
  pageCountUnavailable: 'page count unavailable',
  pageSingular: 'page',
  pagePlural: 'pages',
  replaceFile: 'Replace file',
  removeFile: 'Remove',
  choosePdfTool: 'Choose a PDF tool below. The selected file will open inside that tool.',
  all: 'All',
  popular: 'Popular',
  compressFilter: 'Compress',
  recommendedPdfTools: 'Recommended PDF tools',
  detectedPdf: 'We detected a PDF. What would you like to do?',
  howItWorks: 'How it works',
  howStepChoose: 'Choose or drop your file',
  howStepProcess: 'We process it in your browser',
  howStepDownload: 'Download your result file',
  howStepSecure: 'Fast, secure and easy to use',
  homeTrustFooter: 'PDF911 works offline in your browser. No files are uploaded. Your privacy comes first.',
  faq: 'FAQ',
  commonTasks: 'Common tasks',
  popularSearches: 'Popular searches',
  morePdfTools: 'More PDF tools',
  morePdfToolsLead: 'Jump to related page, compression, conversion and editing tools.',
  allTools: 'All tools',
  current: 'current',
  openingTool: 'Opening tool...',
  loadToolError: 'Could not load the tool',
  checkConnection: 'Check the connection and try opening the tool again.',
  retry: 'Retry',
  openingSettings: 'Opening settings...',
  settingsLoadError: 'Could not load settings:',
  pageLoadError: 'Could not load page:',
  categories: {
    pages: 'Pages',
    optimize: 'Optimize',
    convert: 'Convert',
    edit: 'Edit',
    security: 'Security',
  },
  install: {
    install: 'Install',
    homeScreen: 'Home Screen',
    save: 'Install app',
    installHint: `Install ${BRAND_NAME} as an app`,
    iosHint: `Add ${BRAND_NAME} to the Home Screen`,
    saveHint: `Install ${BRAND_NAME} or save it on this device`,
    iosTitle: `Add ${BRAND_NAME} to the Home Screen`,
    iosLead: 'iOS does not let websites do this automatically. Add the icon from Safari.',
    iosSteps: [
      'Open the site in Safari.',
      'Tap Share.',
      'Choose Add to Home Screen and confirm.',
    ],
    androidTitle: `Install ${BRAND_NAME} on Android`,
    androidLead: 'If the browser did not show the install prompt, add the site from the Chrome menu.',
    androidSteps: [
      'Open the browser menu.',
      'Tap Install app or Add to Home screen.',
      'Confirm the icon.',
    ],
    desktopTitle: `Save ${BRAND_NAME} on this computer`,
    desktopLead: 'Browsers do not let websites add themselves to bookmarks automatically.',
    desktopSteps: (shortcut) => [
      `Press ${shortcut}.`,
      'Choose the bookmarks bar if you want quick access.',
      'In Chrome or Edge, you can also install the site as an app from the address bar or browser menu.',
    ],
  },
};

const UI_COPY_OVERRIDES: Record<string, UiCopyOverride> = {
  ru: {
    languageLabel: 'Язык',
    footer: `${BRAND_NAME} — бесплатные PDF-инструменты для объединения, сжатия, разделения, редактирования и конвертации файлов в браузере.`,
    privacyLink: 'Приватность',
    dataLink: 'Данные',
    close: 'Закрыть',
    analyticsTitle: 'Аналитические cookies',
    analyticsLead: `${BRAND_NAME} использует бережную к приватности аналитику. Файлы не загружаются.`,
    details: 'Подробнее',
    accept: 'Принять',
    switchToLightTheme: 'Переключить на светлую тему',
    switchToDarkTheme: 'Переключить на темную тему',
    lightTheme: 'Светлая тема',
    darkTheme: 'Темная тема',
    homeHeadline: 'Объединяйте, сжимайте и конвертируйте PDF онлайн',
    heroLead: 'Быстрые PDF-инструменты прямо в браузере. Без загрузки файлов на сервер. Ваши документы остаются приватными.',
    privateTitle: 'Приватность по умолчанию',
    privateText: 'Файлы обрабатываются в вашем браузере и не загружаются на наш сервер. Аккаунт не нужен.',
    privacyChip: 'Файлы остаются на вашем устройстве. Приватно и безопасно.',
    pdfTools: 'PDF-инструменты',
    toolFilterLabel: 'Фильтр инструментов',
    homeToolsLead: 'Используйте PDF911, чтобы упорядочивать страницы, объединять документы, сжимать PDF, конвертировать файлы и быстро редактировать без отправки документов на сервер.',
    dropFile: 'Перетащите файлы сюда или выберите файлы',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Выбрать файлы',
    selectedFile: 'Выбранный файл:',
    countingPages: 'считаем страницы...',
    pageCountUnavailable: 'количество страниц недоступно',
    pageSingular: 'страница',
    pagePlural: 'страниц',
    replaceFile: 'Заменить файл',
    removeFile: 'Удалить',
    choosePdfTool: 'Выберите PDF-инструмент ниже. Выбранный файл откроется в этом инструменте.',
    all: 'Все',
    popular: 'Популярное',
    compressFilter: 'Сжать',
    recommendedPdfTools: 'Рекомендуемые PDF-инструменты',
    detectedPdf: 'Мы определили PDF. Что нужно сделать?',
    howItWorks: 'Как это работает',
    howStepChoose: 'Выберите или перетащите файл',
    howStepProcess: 'Мы обрабатываем его в браузере',
    howStepDownload: 'Скачайте готовый файл',
    howStepSecure: 'Быстро, безопасно и просто',
    homeTrustFooter: 'PDF911 работает в браузере. Файлы не загружаются. Приватность на первом месте.',
    faq: 'Частые вопросы',
    commonTasks: 'Частые задачи',
    popularSearches: 'Популярные запросы',
    morePdfTools: 'Еще PDF-инструменты',
    morePdfToolsLead: 'Перейдите к связанным инструментам для страниц, сжатия, конвертации и редактирования.',
    allTools: 'Все инструменты',
    current: 'текущий',
    openingTool: 'Открываем инструмент...',
    loadToolError: 'Не удалось загрузить инструмент',
    checkConnection: 'Проверьте соединение и попробуйте открыть инструмент снова.',
    retry: 'Повторить',
    openingSettings: 'Открываем настройки...',
    settingsLoadError: 'Не удалось загрузить настройки:',
    pageLoadError: 'Не удалось загрузить страницу:',
    categories: { pages: 'Страницы', optimize: 'Оптимизация', convert: 'Конвертация', edit: 'Редактирование', security: 'Безопасность' },
    install: {
      install: 'Установить',
      homeScreen: 'На экран',
      save: 'Установить',
      installHint: `Установить ${BRAND_NAME} как приложение`,
      iosHint: `Добавить ${BRAND_NAME} на экран Домой`,
      saveHint: `Установить ${BRAND_NAME} или сохранить на устройстве`,
      iosTitle: `Добавить ${BRAND_NAME} на экран Домой`,
      androidTitle: `Установить ${BRAND_NAME} на Android`,
      desktopTitle: `Сохранить ${BRAND_NAME} на этом компьютере`,
    },
  },
  'zh-CN': {
    languageLabel: '语言',
    footer: `${BRAND_NAME} — 在浏览器中免费合并、压缩、拆分、编辑和转换 PDF 文件。`,
    privacyLink: '隐私',
    dataLink: '数据',
    close: '关闭',
    analyticsTitle: '分析 Cookie',
    analyticsLead: `${BRAND_NAME} 使用注重隐私的分析。文件不会上传。`,
    details: '详情',
    accept: '接受',
    switchToLightTheme: '切换到浅色主题',
    switchToDarkTheme: '切换到深色主题',
    lightTheme: '浅色主题',
    darkTheme: '深色主题',
    homeHeadline: '在线合并、压缩和转换 PDF',
    heroLead: '快速 PDF 工具，直接在浏览器中运行。无需上传。文件保持私密和安全。',
    privateTitle: '隐私优先设计',
    privateText: '文件在您的浏览器中处理，不会上传到我们的服务器。无需账户。',
    privacyChip: '文件保留在您的设备上。私密且安全。',
    pdfTools: 'PDF 工具',
    toolFilterLabel: '工具筛选',
    homeToolsLead: '使用 PDF911 整理页面、合并文档、压缩 PDF、转换文件，并在不上传到服务器的情况下快速编辑。',
    dropFile: '将文件拖到这里或选择文件',
    fileTypes: 'PDF、Word、Excel、JPG、PNG、CSV',
    chooseFile: '选择文件',
    selectedFile: '已选择文件：',
    countingPages: '正在统计页数...',
    pageCountUnavailable: '无法获取页数',
    pageSingular: '页',
    pagePlural: '页',
    replaceFile: '替换文件',
    removeFile: '移除',
    choosePdfTool: '请在下方选择 PDF 工具。所选文件会在该工具中打开。',
    all: '全部',
    popular: '热门',
    compressFilter: '压缩',
    recommendedPdfTools: '推荐 PDF 工具',
    detectedPdf: '已检测到 PDF。您想做什么？',
    howItWorks: '工作方式',
    howStepChoose: '选择或拖入文件',
    howStepProcess: '在浏览器中处理',
    howStepDownload: '下载结果文件',
    howStepSecure: '快速、安全、易用',
    homeTrustFooter: 'PDF911 可在浏览器中离线工作。文件不会上传。隐私优先。',
    faq: '常见问题',
    commonTasks: '常见任务',
    popularSearches: '热门搜索',
    morePdfTools: '更多 PDF 工具',
    morePdfToolsLead: '跳转到相关的页面、压缩、转换和编辑工具。',
    allTools: '全部工具',
    current: '当前',
    openingTool: '正在打开工具...',
    loadToolError: '无法加载工具',
    checkConnection: '请检查连接并再次打开工具。',
    retry: '重试',
    openingSettings: '正在打开设置...',
    settingsLoadError: '无法加载设置：',
    pageLoadError: '无法加载页面：',
    categories: { pages: '页面', optimize: '优化', convert: '转换', edit: '编辑', security: '安全' },
    install: {
      install: '安装',
      homeScreen: '主屏幕',
      save: '安装应用',
      installHint: `将 ${BRAND_NAME} 安装为应用`,
      iosHint: `将 ${BRAND_NAME} 添加到主屏幕`,
      saveHint: `安装 ${BRAND_NAME} 或保存到此设备`,
      iosTitle: `将 ${BRAND_NAME} 添加到主屏幕`,
      androidTitle: `在 Android 上安装 ${BRAND_NAME}`,
      desktopTitle: `在此电脑上保存 ${BRAND_NAME}`,
    },
  },
  es: {
    languageLabel: 'Idioma',
    footer: `${BRAND_NAME} — herramientas PDF gratis para unir, comprimir, dividir, editar y convertir archivos en el navegador.`,
    privacyLink: 'Privacidad',
    dataLink: 'Datos',
    close: 'Cerrar',
    analyticsTitle: 'Cookies de analitica',
    analyticsLead: `${BRAND_NAME} usa analitica respetuosa con la privacidad. Los archivos no se suben.`,
    details: 'Detalles',
    accept: 'Aceptar',
    switchToLightTheme: 'Cambiar al tema claro',
    switchToDarkTheme: 'Cambiar al tema oscuro',
    lightTheme: 'Tema claro',
    darkTheme: 'Tema oscuro',
    homeHeadline: 'Unir, comprimir y convertir PDF online',
    heroLead: 'Herramientas PDF rapidas que funcionan en tu navegador. Sin subidas. Tus archivos permanecen privados y seguros.',
    privateTitle: 'Privado por diseno',
    privateText: 'Los archivos se procesan en tu navegador y no se suben a nuestro servidor. No hace falta cuenta.',
    privacyChip: 'Los archivos permanecen en tu dispositivo. Privado y seguro.',
    pdfTools: 'Herramientas PDF',
    toolFilterLabel: 'Filtro de herramientas',
    homeToolsLead: 'Usa PDF911 para organizar paginas, unir documentos, comprimir PDFs, convertir archivos y hacer ediciones rapidas sin enviar documentos a un servidor.',
    dropFile: 'Suelta archivos aqui o elige archivos',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Elegir archivos',
    selectedFile: 'Archivo seleccionado:',
    countingPages: 'contando paginas...',
    pageCountUnavailable: 'conteo de paginas no disponible',
    pageSingular: 'pagina',
    pagePlural: 'paginas',
    replaceFile: 'Cambiar archivo',
    removeFile: 'Quitar',
    choosePdfTool: 'Elige una herramienta PDF abajo. El archivo seleccionado se abrira dentro de esa herramienta.',
    all: 'Todo',
    popular: 'Popular',
    compressFilter: 'Comprimir',
    recommendedPdfTools: 'Herramientas PDF recomendadas',
    detectedPdf: 'Detectamos un PDF. Que quieres hacer?',
    howItWorks: 'Como funciona',
    howStepChoose: 'Elige o suelta tu archivo',
    howStepProcess: 'Lo procesamos en tu navegador',
    howStepDownload: 'Descarga el resultado',
    howStepSecure: 'Rapido, seguro y facil',
    homeTrustFooter: 'PDF911 funciona sin conexion en tu navegador. Los archivos no se suben. Tu privacidad va primero.',
    faq: 'Preguntas frecuentes',
    commonTasks: 'Tareas comunes',
    popularSearches: 'Busquedas populares',
    morePdfTools: 'Mas herramientas PDF',
    morePdfToolsLead: 'Ve a herramientas relacionadas para paginas, compresion, conversion y edicion.',
    allTools: 'Todas las herramientas',
    current: 'actual',
    openingTool: 'Abriendo herramienta...',
    loadToolError: 'No se pudo cargar la herramienta',
    checkConnection: 'Comprueba la conexion e intenta abrir la herramienta de nuevo.',
    retry: 'Reintentar',
    openingSettings: 'Abriendo ajustes...',
    settingsLoadError: 'No se pudieron cargar los ajustes:',
    pageLoadError: 'No se pudo cargar la pagina:',
    categories: { pages: 'Paginas', optimize: 'Optimizar', convert: 'Convertir', edit: 'Editar', security: 'Seguridad' },
    install: {
      install: 'Instalar',
      homeScreen: 'Pantalla inicio',
      save: 'Guardar',
      installHint: `Instalar ${BRAND_NAME} como app`,
      iosHint: `Anadir ${BRAND_NAME} a la pantalla de inicio`,
      saveHint: `Guardar ${BRAND_NAME} en este dispositivo`,
      iosTitle: `Anadir ${BRAND_NAME} a la pantalla de inicio`,
      iosLead: 'iOS no permite hacerlo automaticamente. Anade el icono desde Safari.',
      iosSteps: ['Abre el sitio en Safari.', 'Toca Compartir.', 'Elige Anadir a pantalla de inicio y confirma.'],
      androidTitle: `Instalar ${BRAND_NAME} en Android`,
      androidLead: 'Si el navegador no mostro el aviso de instalacion, anade el sitio desde el menu de Chrome.',
      androidSteps: ['Abre el menu del navegador.', 'Toca Instalar app o Anadir a pantalla de inicio.', 'Confirma el icono.'],
      desktopTitle: `Guardar ${BRAND_NAME} en este equipo`,
      desktopLead: 'Los navegadores no permiten que un sitio se agregue solo a favoritos.',
      desktopSteps: (shortcut) => [`Pulsa ${shortcut}.`, 'Elige la barra de marcadores si quieres acceso rapido.', 'En Chrome o Edge tambien puedes instalar el sitio como app desde la barra de direcciones o el menu.'],
    },
  },
  'pt-BR': {
    languageLabel: 'Idioma',
    footer: `${BRAND_NAME} — ferramentas PDF gratis para juntar, comprimir, dividir, editar e converter arquivos no navegador.`,
    privacyLink: 'Privacidade',
    dataLink: 'Dados',
    close: 'Fechar',
    analyticsTitle: 'Cookies de analise',
    analyticsLead: `${BRAND_NAME} usa analise com foco em privacidade. Os arquivos nao sao enviados.`,
    details: 'Detalhes',
    accept: 'Aceitar',
    switchToLightTheme: 'Mudar para tema claro',
    switchToDarkTheme: 'Mudar para tema escuro',
    lightTheme: 'Tema claro',
    darkTheme: 'Tema escuro',
    homeHeadline: 'Juntar, comprimir e converter PDF online',
    heroLead: 'Ferramentas PDF rapidas que funcionam no navegador. Sem uploads. Seus arquivos ficam privados e seguros.',
    privateTitle: 'Privado por design',
    privateText: 'Os arquivos sao processados no navegador e nao sao enviados ao nosso servidor. Nao precisa de conta.',
    privacyChip: 'Os arquivos ficam no seu dispositivo. Privado e seguro.',
    pdfTools: 'Ferramentas PDF',
    toolFilterLabel: 'Filtro de ferramentas',
    homeToolsLead: 'Use PDF911 para organizar paginas, juntar documentos, comprimir PDFs, converter arquivos e fazer edicoes rapidas sem enviar documentos a um servidor.',
    dropFile: 'Solte arquivos aqui ou escolha arquivos',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Escolher arquivos',
    selectedFile: 'Arquivo selecionado:',
    countingPages: 'contando paginas...',
    pageCountUnavailable: 'contagem de paginas indisponivel',
    pageSingular: 'pagina',
    pagePlural: 'paginas',
    replaceFile: 'Trocar arquivo',
    removeFile: 'Remover',
    choosePdfTool: 'Escolha uma ferramenta PDF abaixo. O arquivo selecionado abrira nessa ferramenta.',
    all: 'Tudo',
    popular: 'Popular',
    compressFilter: 'Comprimir',
    recommendedPdfTools: 'Ferramentas PDF recomendadas',
    detectedPdf: 'Detectamos um PDF. O que voce quer fazer?',
    howItWorks: 'Como funciona',
    howStepChoose: 'Escolha ou solte seu arquivo',
    howStepProcess: 'Processamos no navegador',
    howStepDownload: 'Baixe o resultado',
    howStepSecure: 'Rapido, seguro e facil',
    homeTrustFooter: 'PDF911 funciona offline no navegador. Os arquivos nao sao enviados. Sua privacidade vem primeiro.',
    faq: 'Perguntas frequentes',
    commonTasks: 'Tarefas comuns',
    popularSearches: 'Buscas populares',
    morePdfTools: 'Mais ferramentas PDF',
    morePdfToolsLead: 'Acesse ferramentas relacionadas de paginas, compressao, conversao e edicao.',
    allTools: 'Todas as ferramentas',
    current: 'atual',
    openingTool: 'Abrindo ferramenta...',
    loadToolError: 'Nao foi possivel carregar a ferramenta',
    checkConnection: 'Verifique a conexao e tente abrir a ferramenta novamente.',
    retry: 'Tentar novamente',
    openingSettings: 'Abrindo configuracoes...',
    settingsLoadError: 'Nao foi possivel carregar as configuracoes:',
    pageLoadError: 'Nao foi possivel carregar a pagina:',
    categories: { pages: 'Paginas', optimize: 'Otimizar', convert: 'Converter', edit: 'Editar', security: 'Seguranca' },
    install: {
      install: 'Instalar',
      homeScreen: 'Tela inicial',
      save: 'Salvar',
      installHint: `Instalar ${BRAND_NAME} como app`,
      iosHint: `Adicionar ${BRAND_NAME} a tela inicial`,
      saveHint: `Salvar ${BRAND_NAME} neste dispositivo`,
      iosTitle: `Adicionar ${BRAND_NAME} a tela inicial`,
      androidTitle: `Instalar ${BRAND_NAME} no Android`,
      desktopTitle: `Salvar ${BRAND_NAME} neste computador`,
    },
  },
  de: {
    languageLabel: 'Sprache',
    footer: `${BRAND_NAME} — kostenlose PDF-Tools zum Zusammenfugen, Komprimieren, Teilen, Bearbeiten und Konvertieren im Browser.`,
    privacyLink: 'Datenschutz',
    dataLink: 'Daten',
    close: 'Schliessen',
    analyticsTitle: 'Analyse-Cookies',
    analyticsLead: `${BRAND_NAME} verwendet datenschutzfreundliche Analyse. Dateien werden nicht hochgeladen.`,
    details: 'Details',
    accept: 'Akzeptieren',
    switchToLightTheme: 'Zum hellen Design wechseln',
    switchToDarkTheme: 'Zum dunklen Design wechseln',
    lightTheme: 'Helles Design',
    darkTheme: 'Dunkles Design',
    homeHeadline: 'PDF online zusammenfugen, komprimieren und konvertieren',
    heroLead: 'Schnelle PDF-Tools direkt im Browser. Keine Uploads. Ihre Dateien bleiben privat und sicher.',
    privateTitle: 'Privat entwickelt',
    privateText: 'Dateien werden in Ihrem Browser verarbeitet und nicht auf unseren Server hochgeladen. Kein Konto erforderlich.',
    privacyChip: 'Dateien bleiben auf Ihrem Gerat. Privat und sicher.',
    pdfTools: 'PDF-Tools',
    toolFilterLabel: 'Tool-Filter',
    homeToolsLead: 'Nutzen Sie PDF911, um Seiten zu organisieren, Dokumente zusammenzufugen, PDFs zu komprimieren, Dateien zu konvertieren und schnelle Bearbeitungen ohne Server-Upload vorzunehmen.',
    dropFile: 'Dateien hier ablegen oder auswahlen',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Dateien auswahlen',
    selectedFile: 'Ausgewahlte Datei:',
    countingPages: 'Seiten werden gezahlt...',
    pageCountUnavailable: 'Seitenzahl nicht verfugbar',
    pageSingular: 'Seite',
    pagePlural: 'Seiten',
    replaceFile: 'Datei ersetzen',
    removeFile: 'Entfernen',
    choosePdfTool: 'Wahlen Sie unten ein PDF-Tool. Die ausgewahlte Datei wird in diesem Tool geoffnet.',
    all: 'Alle',
    popular: 'Beliebt',
    compressFilter: 'Komprimieren',
    recommendedPdfTools: 'Empfohlene PDF-Tools',
    detectedPdf: 'Wir haben eine PDF erkannt. Was mochten Sie tun?',
    howItWorks: 'So funktioniert es',
    howStepChoose: 'Datei auswahlen oder ablegen',
    howStepProcess: 'Verarbeitung im Browser',
    howStepDownload: 'Ergebnis herunterladen',
    howStepSecure: 'Schnell, sicher und einfach',
    homeTrustFooter: 'PDF911 funktioniert offline im Browser. Dateien werden nicht hochgeladen. Ihre Privatsphare steht an erster Stelle.',
    faq: 'Häufige Fragen',
    commonTasks: 'Haufige Aufgaben',
    popularSearches: 'Beliebte Suchanfragen',
    morePdfTools: 'Weitere PDF-Tools',
    morePdfToolsLead: 'Zu verwandten Tools fur Seiten, Komprimierung, Konvertierung und Bearbeitung springen.',
    allTools: 'Alle Tools',
    current: 'aktuell',
    openingTool: 'Tool wird geoffnet...',
    loadToolError: 'Das Tool konnte nicht geladen werden',
    checkConnection: 'Prufen Sie die Verbindung und offnen Sie das Tool erneut.',
    retry: 'Erneut versuchen',
    openingSettings: 'Einstellungen werden geoffnet...',
    settingsLoadError: 'Einstellungen konnten nicht geladen werden:',
    pageLoadError: 'Seite konnte nicht geladen werden:',
    categories: { pages: 'Seiten', optimize: 'Optimieren', convert: 'Konvertieren', edit: 'Bearbeiten', security: 'Sicherheit' },
    install: {
      install: 'Installieren',
      homeScreen: 'Startbildschirm',
      save: 'Speichern',
      installHint: `${BRAND_NAME} als App installieren`,
      iosHint: `${BRAND_NAME} zum Startbildschirm hinzufugen`,
      saveHint: `${BRAND_NAME} auf diesem Gerat speichern`,
      iosTitle: `${BRAND_NAME} zum Startbildschirm hinzufugen`,
      androidTitle: `${BRAND_NAME} auf Android installieren`,
      desktopTitle: `${BRAND_NAME} auf diesem Computer speichern`,
    },
  },
  fr: {
    languageLabel: 'Langue',
    footer: `${BRAND_NAME} — outils PDF gratuits pour fusionner, compresser, diviser, modifier et convertir dans le navigateur.`,
    privacyLink: 'Confidentialite',
    dataLink: 'Donnees',
    close: 'Fermer',
    analyticsTitle: 'Cookies analytiques',
    analyticsLead: `${BRAND_NAME} utilise une analyse respectueuse de la vie privee. Les fichiers ne sont pas envoyes.`,
    details: 'Details',
    accept: 'Accepter',
    switchToLightTheme: 'Passer au theme clair',
    switchToDarkTheme: 'Passer au theme sombre',
    lightTheme: 'Theme clair',
    darkTheme: 'Theme sombre',
    homeHeadline: 'Fusionner, compresser et convertir des PDF en ligne',
    heroLead: 'Des outils PDF rapides dans votre navigateur. Aucun envoi. Vos fichiers restent prives et securises.',
    privateTitle: 'Prive par conception',
    privateText: 'Les fichiers sont traites dans votre navigateur et ne sont pas envoyes a notre serveur. Aucun compte requis.',
    privacyChip: 'Les fichiers restent sur votre appareil. Prive et securise.',
    pdfTools: 'Outils PDF',
    toolFilterLabel: 'Filtre des outils',
    homeToolsLead: 'Utilisez PDF911 pour organiser les pages, fusionner des documents, compresser des PDF, convertir des fichiers et faire des edits rapides sans envoyer les documents a un serveur.',
    dropFile: 'Deposez des fichiers ici ou choisissez-les',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Choisir des fichiers',
    selectedFile: 'Fichier selectionne:',
    countingPages: 'comptage des pages...',
    pageCountUnavailable: 'nombre de pages indisponible',
    pageSingular: 'page',
    pagePlural: 'pages',
    replaceFile: 'Remplacer le fichier',
    removeFile: 'Supprimer',
    choosePdfTool: 'Choisissez un outil PDF ci-dessous. Le fichier selectionne s ouvrira dans cet outil.',
    all: 'Tous',
    popular: 'Populaire',
    compressFilter: 'Compresser',
    recommendedPdfTools: 'Outils PDF recommandes',
    detectedPdf: 'Nous avons detecte un PDF. Que voulez-vous faire?',
    howItWorks: 'Comment ca marche',
    howStepChoose: 'Choisissez ou deposez votre fichier',
    howStepProcess: 'Traitement dans le navigateur',
    howStepDownload: 'Telechargez le resultat',
    howStepSecure: 'Rapide, securise et simple',
    homeTrustFooter: 'PDF911 fonctionne hors ligne dans le navigateur. Les fichiers ne sont pas envoyes. Votre confidentialite passe avant tout.',
    faq: 'Questions fréquentes',
    commonTasks: 'Taches courantes',
    popularSearches: 'Recherches populaires',
    morePdfTools: 'Plus outils PDF',
    morePdfToolsLead: 'Accedez aux outils lies aux pages, a la compression, a la conversion et a la modification.',
    allTools: 'Tous les outils',
    current: 'actuel',
    openingTool: 'Ouverture de l outil...',
    loadToolError: 'Impossible de charger l outil',
    checkConnection: 'Verifiez la connexion et essayez d ouvrir l outil a nouveau.',
    retry: 'Reessayer',
    openingSettings: 'Ouverture des reglages...',
    settingsLoadError: 'Impossible de charger les reglages:',
    pageLoadError: 'Impossible de charger la page:',
    categories: { pages: 'Pages', optimize: 'Optimiser', convert: 'Convertir', edit: 'Modifier', security: 'Securite' },
    install: {
      install: 'Installer',
      homeScreen: 'Ecran accueil',
      save: 'Enregistrer',
      installHint: `Installer ${BRAND_NAME} comme application`,
      iosHint: `Ajouter ${BRAND_NAME} a l ecran d accueil`,
      saveHint: `Enregistrer ${BRAND_NAME} sur cet appareil`,
      iosTitle: `Ajouter ${BRAND_NAME} a l ecran d accueil`,
      androidTitle: `Installer ${BRAND_NAME} sur Android`,
      desktopTitle: `Enregistrer ${BRAND_NAME} sur cet ordinateur`,
    },
  },
  it: {
    languageLabel: 'Lingua',
    footer: `${BRAND_NAME} — strumenti PDF gratis per unire, comprimere, dividere, modificare e convertire nel browser.`,
    privacyLink: 'Privacy',
    dataLink: 'Dati',
    close: 'Chiudi',
    analyticsTitle: 'Cookie di analisi',
    analyticsLead: `${BRAND_NAME} usa analisi attente alla privacy. I file non vengono caricati.`,
    details: 'Dettagli',
    accept: 'Accetta',
    switchToLightTheme: 'Passa al tema chiaro',
    switchToDarkTheme: 'Passa al tema scuro',
    lightTheme: 'Tema chiaro',
    darkTheme: 'Tema scuro',
    homeHeadline: 'Unire, comprimere e convertire PDF online',
    heroLead: 'Strumenti PDF rapidi nel browser. Nessun upload. I file restano privati e sicuri.',
    privateTitle: 'Privato per design',
    privateText: 'I file vengono elaborati nel browser e non caricati sul nostro server. Nessun account richiesto.',
    privacyChip: 'I file restano sul tuo dispositivo. Privato e sicuro.',
    pdfTools: 'Strumenti PDF',
    toolFilterLabel: 'Filtro strumenti',
    homeToolsLead: 'Usa PDF911 per organizzare pagine, unire documenti, comprimere PDF, convertire file e fare modifiche rapide senza inviare documenti a un server.',
    dropFile: 'Rilascia file qui o scegli file',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Scegli file',
    selectedFile: 'File selezionato:',
    countingPages: 'conteggio pagine...',
    pageCountUnavailable: 'conteggio pagine non disponibile',
    pageSingular: 'pagina',
    pagePlural: 'pagine',
    replaceFile: 'Sostituisci file',
    removeFile: 'Rimuovi',
    choosePdfTool: 'Scegli uno strumento PDF qui sotto. Il file selezionato si aprira in quello strumento.',
    all: 'Tutti',
    popular: 'Popolari',
    compressFilter: 'Comprimi',
    recommendedPdfTools: 'Strumenti PDF consigliati',
    detectedPdf: 'Abbiamo rilevato un PDF. Cosa vuoi fare?',
    howItWorks: 'Come funziona',
    howStepChoose: 'Scegli o rilascia il file',
    howStepProcess: 'Lo elaboriamo nel browser',
    howStepDownload: 'Scarica il risultato',
    howStepSecure: 'Rapido, sicuro e semplice',
    homeTrustFooter: 'PDF911 funziona offline nel browser. I file non vengono caricati. La tua privacy viene prima.',
    faq: 'Domande frequenti',
    commonTasks: 'Attivita comuni',
    popularSearches: 'Ricerche popolari',
    morePdfTools: 'Altri strumenti PDF',
    morePdfToolsLead: 'Vai agli strumenti correlati per pagine, compressione, conversione e modifica.',
    allTools: 'Tutti gli strumenti',
    current: 'attuale',
    openingTool: 'Apertura strumento...',
    loadToolError: 'Impossibile caricare lo strumento',
    checkConnection: 'Controlla la connessione e prova ad aprire di nuovo lo strumento.',
    retry: 'Riprova',
    openingSettings: 'Apertura impostazioni...',
    settingsLoadError: 'Impossibile caricare le impostazioni:',
    pageLoadError: 'Impossibile caricare la pagina:',
    categories: { pages: 'Pagine', optimize: 'Ottimizza', convert: 'Converti', edit: 'Modifica', security: 'Sicurezza' },
    install: {
      install: 'Installa',
      homeScreen: 'Schermata Home',
      save: 'Salva',
      installHint: `Installa ${BRAND_NAME} come app`,
      iosHint: `Aggiungi ${BRAND_NAME} alla schermata Home`,
      saveHint: `Salva ${BRAND_NAME} su questo dispositivo`,
      iosTitle: `Aggiungi ${BRAND_NAME} alla schermata Home`,
      androidTitle: `Installa ${BRAND_NAME} su Android`,
      desktopTitle: `Salva ${BRAND_NAME} su questo computer`,
    },
  },
  nl: {
    languageLabel: 'Taal',
    footer: `${BRAND_NAME} — gratis PDF-tools om bestanden in de browser samen te voegen, comprimeren, splitsen, bewerken en converteren.`,
    privacyLink: 'Privacy',
    dataLink: 'Gegevens',
    close: 'Sluiten',
    analyticsTitle: 'Analytics-cookies',
    analyticsLead: `${BRAND_NAME} gebruikt privacyvriendelijke analytics. Bestanden worden niet geupload.`,
    details: 'Details',
    accept: 'Accepteren',
    switchToLightTheme: 'Naar licht thema',
    switchToDarkTheme: 'Naar donker thema',
    lightTheme: 'Licht thema',
    darkTheme: 'Donker thema',
    homeHeadline: 'PDF online samenvoegen, comprimeren en converteren',
    heroLead: 'Snelle PDF-tools direct in uw browser. Geen uploads. Uw bestanden blijven prive en veilig.',
    privateTitle: 'Prive ontworpen',
    privateText: 'Bestanden worden in uw browser verwerkt en niet naar onze server geupload. Geen account nodig.',
    privacyChip: 'Bestanden blijven op uw apparaat. Prive en veilig.',
    pdfTools: 'PDF-tools',
    toolFilterLabel: 'Toolfilter',
    homeToolsLead: 'Gebruik PDF911 om paginas te organiseren, documenten samen te voegen, PDFs te comprimeren, bestanden te converteren en snel te bewerken zonder documenten naar een server te sturen.',
    dropFile: 'Sleep bestanden hier of kies bestanden',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Bestanden kiezen',
    selectedFile: 'Geselecteerd bestand:',
    countingPages: 'paginas tellen...',
    pageCountUnavailable: 'aantal paginas niet beschikbaar',
    pageSingular: 'pagina',
    pagePlural: 'paginas',
    replaceFile: 'Bestand vervangen',
    removeFile: 'Verwijderen',
    choosePdfTool: 'Kies hieronder een PDF-tool. Het geselecteerde bestand opent in die tool.',
    all: 'Alles',
    popular: 'Populair',
    compressFilter: 'Comprimeren',
    recommendedPdfTools: 'Aanbevolen PDF-tools',
    detectedPdf: 'We hebben een PDF gevonden. Wat wilt u doen?',
    howItWorks: 'Hoe het werkt',
    howStepChoose: 'Kies of sleep uw bestand',
    howStepProcess: 'Wij verwerken het in uw browser',
    howStepDownload: 'Download het resultaat',
    howStepSecure: 'Snel, veilig en eenvoudig',
    homeTrustFooter: 'PDF911 werkt offline in uw browser. Bestanden worden niet geupload. Uw privacy staat voorop.',
    faq: 'Veelgestelde vragen',
    commonTasks: 'Veelvoorkomende taken',
    popularSearches: 'Populaire zoekopdrachten',
    morePdfTools: 'Meer PDF-tools',
    morePdfToolsLead: 'Ga naar gerelateerde tools voor paginas, compressie, conversie en bewerking.',
    allTools: 'Alle tools',
    current: 'huidig',
    openingTool: 'Tool openen...',
    loadToolError: 'Tool kon niet worden geladen',
    checkConnection: 'Controleer de verbinding en open de tool opnieuw.',
    retry: 'Opnieuw',
    openingSettings: 'Instellingen openen...',
    settingsLoadError: 'Instellingen konden niet worden geladen:',
    pageLoadError: 'Pagina kon niet worden geladen:',
    categories: { pages: 'Paginas', optimize: 'Optimaliseren', convert: 'Converteren', edit: 'Bewerken', security: 'Beveiliging' },
    install: {
      install: 'Installeren',
      homeScreen: 'Startscherm',
      save: 'Opslaan',
      installHint: `${BRAND_NAME} als app installeren`,
      iosHint: `${BRAND_NAME} aan het startscherm toevoegen`,
      saveHint: `${BRAND_NAME} op dit apparaat opslaan`,
      iosTitle: `${BRAND_NAME} aan het startscherm toevoegen`,
      androidTitle: `${BRAND_NAME} op Android installeren`,
      desktopTitle: `${BRAND_NAME} op deze computer opslaan`,
    },
  },
  pl: {
    languageLabel: 'Jezyk',
    footer: `${BRAND_NAME} — darmowe narzedzia PDF do laczenia, kompresji, dzielenia, edycji i konwersji w przegladarce.`,
    privacyLink: 'Prywatnosc',
    dataLink: 'Dane',
    close: 'Zamknij',
    analyticsTitle: 'Cookies analityczne',
    analyticsLead: `${BRAND_NAME} uzywa analityki przyjaznej prywatnosci. Pliki nie sa przesylane.`,
    details: 'Szczegoly',
    accept: 'Akceptuj',
    switchToLightTheme: 'Przelacz na jasny motyw',
    switchToDarkTheme: 'Przelacz na ciemny motyw',
    lightTheme: 'Jasny motyw',
    darkTheme: 'Ciemny motyw',
    homeHeadline: 'Lacz, kompresuj i konwertuj PDF online',
    heroLead: 'Szybkie narzedzia PDF w przegladarce. Bez wysylania. Pliki pozostaja prywatne i bezpieczne.',
    privateTitle: 'Prywatnosc w projekcie',
    privateText: 'Pliki sa przetwarzane w przegladarce i nie trafiaja na nasz serwer. Konto nie jest wymagane.',
    privacyChip: 'Pliki pozostaja na Twoim urzadzeniu. Prywatnie i bezpiecznie.',
    pdfTools: 'Narzedzia PDF',
    toolFilterLabel: 'Filtr narzedzi',
    homeToolsLead: 'Uzyj PDF911, aby organizowac strony, laczyc dokumenty, kompresowac PDF, konwertowac pliki i szybko edytowac bez wysylania dokumentow na serwer.',
    dropFile: 'Upusc pliki tutaj lub wybierz pliki',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Wybierz pliki',
    selectedFile: 'Wybrany plik:',
    countingPages: 'liczenie stron...',
    pageCountUnavailable: 'liczba stron niedostepna',
    pageSingular: 'strona',
    pagePlural: 'stron',
    replaceFile: 'Zmien plik',
    removeFile: 'Usun',
    choosePdfTool: 'Wybierz narzedzie PDF ponizej. Wybrany plik otworzy sie w tym narzedziu.',
    all: 'Wszystko',
    popular: 'Popularne',
    compressFilter: 'Kompresuj',
    recommendedPdfTools: 'Polecane narzedzia PDF',
    detectedPdf: 'Wykrylismy PDF. Co chcesz zrobic?',
    howItWorks: 'Jak to dziala',
    howStepChoose: 'Wybierz lub upusc plik',
    howStepProcess: 'Przetwarzamy w przegladarce',
    howStepDownload: 'Pobierz wynik',
    howStepSecure: 'Szybko, bezpiecznie i prosto',
    homeTrustFooter: 'PDF911 dziala offline w przegladarce. Pliki nie sa wysylane. Prywatnosc jest najwazniejsza.',
    faq: 'Najczęstsze pytania',
    commonTasks: 'Typowe zadania',
    popularSearches: 'Popularne wyszukiwania',
    morePdfTools: 'Wiecej narzedzi PDF',
    morePdfToolsLead: 'Przejdz do powiazanych narzedzi stron, kompresji, konwersji i edycji.',
    allTools: 'Wszystkie narzedzia',
    current: 'aktualne',
    openingTool: 'Otwieranie narzedzia...',
    loadToolError: 'Nie udalo sie zaladowac narzedzia',
    checkConnection: 'Sprawdz polaczenie i sprobuj otworzyc narzedzie ponownie.',
    retry: 'Ponow',
    openingSettings: 'Otwieranie ustawien...',
    settingsLoadError: 'Nie udalo sie zaladowac ustawien:',
    pageLoadError: 'Nie udalo sie zaladowac strony:',
    categories: { pages: 'Strony', optimize: 'Optymalizacja', convert: 'Konwersja', edit: 'Edycja', security: 'Bezpieczenstwo' },
    install: {
      install: 'Zainstaluj',
      homeScreen: 'Ekran glowny',
      save: 'Zapisz',
      installHint: `Zainstaluj ${BRAND_NAME} jako aplikacje`,
      iosHint: `Dodaj ${BRAND_NAME} do ekranu glownego`,
      saveHint: `Zapisz ${BRAND_NAME} na tym urzadzeniu`,
      iosTitle: `Dodaj ${BRAND_NAME} do ekranu glownego`,
      androidTitle: `Zainstaluj ${BRAND_NAME} na Androidzie`,
      desktopTitle: `Zapisz ${BRAND_NAME} na tym komputerze`,
    },
  },
  tr: {
    languageLabel: 'Dil',
    footer: `${BRAND_NAME} — tarayicida PDF birlestirme, sikistirma, bolme, duzenleme ve donusturme icin ucretsiz araclar.`,
    privacyLink: 'Gizlilik',
    dataLink: 'Veri',
    close: 'Kapat',
    analyticsTitle: 'Analitik cerezleri',
    analyticsLead: `${BRAND_NAME} gizlilige uygun analitik kullanir. Dosyalar yuklenmez.`,
    details: 'Ayrintilar',
    accept: 'Kabul et',
    switchToLightTheme: 'Acik temaya gec',
    switchToDarkTheme: 'Koyu temaya gec',
    lightTheme: 'Acik tema',
    darkTheme: 'Koyu tema',
    homeHeadline: 'PDF online birlestir, sikistir ve donustur',
    heroLead: 'Tarayicinizda calisan hizli PDF araclari. Yukleme yok. Dosyalariniz gizli ve guvende kalir.',
    privateTitle: 'Gizlilik odakli',
    privateText: 'Dosyalar tarayicinizda islenir ve sunucumuza yuklenmez. Hesap gerekmez.',
    privacyChip: 'Dosyalar cihazinizda kalir. Gizli ve guvenli.',
    pdfTools: 'PDF araclari',
    toolFilterLabel: 'Arac filtresi',
    homeToolsLead: 'PDF911 ile sayfalari duzenleyin, belgeleri birlestirin, PDFleri sikistirin, dosyalari donusturun ve belgeleri sunucuya gondermeden hizli duzenlemeler yapin.',
    dropFile: 'Dosyalari buraya birak veya sec',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Dosya sec',
    selectedFile: 'Secili dosya:',
    countingPages: 'sayfalar sayiliyor...',
    pageCountUnavailable: 'sayfa sayisi yok',
    pageSingular: 'sayfa',
    pagePlural: 'sayfa',
    replaceFile: 'Dosyayi degistir',
    removeFile: 'Kaldir',
    choosePdfTool: 'Asagidan bir PDF araci secin. Secili dosya o aracta acilir.',
    all: 'Tumu',
    popular: 'Populer',
    compressFilter: 'Sikistir',
    recommendedPdfTools: 'Onerilen PDF araclari',
    detectedPdf: 'Bir PDF algiladik. Ne yapmak istersiniz?',
    howItWorks: 'Nasil calisir',
    howStepChoose: 'Dosyanizi secin veya birakin',
    howStepProcess: 'Tarayicida isliyoruz',
    howStepDownload: 'Sonucu indirin',
    howStepSecure: 'Hizli, guvenli ve kolay',
    homeTrustFooter: 'PDF911 tarayicinizda cevrimdisi calisir. Dosyalar yuklenmez. Gizliliginiz once gelir.',
    faq: 'Sık sorulan sorular',
    commonTasks: 'Yaygin islemler',
    popularSearches: 'Populer aramalar',
    morePdfTools: 'Daha fazla PDF araci',
    morePdfToolsLead: 'Sayfa, sikistirma, donusturme ve duzenleme araclarina gecin.',
    allTools: 'Tum araclar',
    current: 'gecerli',
    openingTool: 'Arac aciliyor...',
    loadToolError: 'Arac yuklenemedi',
    checkConnection: 'Baglantiyi kontrol edin ve araci tekrar acmayi deneyin.',
    retry: 'Tekrar dene',
    openingSettings: 'Ayarlar aciliyor...',
    settingsLoadError: 'Ayarlar yuklenemedi:',
    pageLoadError: 'Sayfa yuklenemedi:',
    categories: { pages: 'Sayfalar', optimize: 'Optimize', convert: 'Donustur', edit: 'Duzenle', security: 'Guvenlik' },
    install: {
      install: 'Yukle',
      homeScreen: 'Ana ekran',
      save: 'Kaydet',
      installHint: `${BRAND_NAME} uygulama olarak yukle`,
      iosHint: `${BRAND_NAME} ana ekrana ekle`,
      saveHint: `${BRAND_NAME} bu cihaza kaydet`,
      iosTitle: `${BRAND_NAME} ana ekrana ekle`,
      androidTitle: `${BRAND_NAME} Android e yukle`,
      desktopTitle: `${BRAND_NAME} bu bilgisayara kaydet`,
    },
  },
  id: {
    languageLabel: 'Bahasa',
    footer: `${BRAND_NAME} — alat PDF gratis untuk menggabungkan, mengompres, memisahkan, mengedit, dan mengonversi file di browser.`,
    privacyLink: 'Privasi',
    dataLink: 'Data',
    close: 'Tutup',
    analyticsTitle: 'Cookie analitik',
    analyticsLead: `${BRAND_NAME} memakai analitik ramah privasi. File tidak diunggah.`,
    details: 'Detail',
    accept: 'Terima',
    switchToLightTheme: 'Beralih ke tema terang',
    switchToDarkTheme: 'Beralih ke tema gelap',
    lightTheme: 'Tema terang',
    darkTheme: 'Tema gelap',
    homeHeadline: 'Gabungkan, kompres, dan konversi PDF online',
    heroLead: 'Alat PDF cepat yang bekerja di browser Anda. Tanpa unggahan. File tetap privat dan aman.',
    privateTitle: 'Privat sejak desain',
    privateText: 'File diproses di browser Anda dan tidak diunggah ke server kami. Tidak perlu akun.',
    privacyChip: 'File tetap di perangkat Anda. Privat dan aman.',
    pdfTools: 'Alat PDF',
    toolFilterLabel: 'Filter alat',
    homeToolsLead: 'Gunakan PDF911 untuk mengatur halaman, menggabungkan dokumen, mengompres PDF, mengonversi file, dan melakukan edit cepat tanpa mengirim dokumen ke server.',
    dropFile: 'Letakkan file di sini atau pilih file',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Pilih file',
    selectedFile: 'File terpilih:',
    countingPages: 'menghitung halaman...',
    pageCountUnavailable: 'jumlah halaman tidak tersedia',
    pageSingular: 'halaman',
    pagePlural: 'halaman',
    replaceFile: 'Ganti file',
    removeFile: 'Hapus',
    choosePdfTool: 'Pilih alat PDF di bawah. File terpilih akan dibuka di alat tersebut.',
    all: 'Semua',
    popular: 'Populer',
    compressFilter: 'Kompres',
    recommendedPdfTools: 'Alat PDF yang disarankan',
    detectedPdf: 'Kami mendeteksi PDF. Apa yang ingin Anda lakukan?',
    howItWorks: 'Cara kerja',
    howStepChoose: 'Pilih atau letakkan file',
    howStepProcess: 'Kami proses di browser',
    howStepDownload: 'Unduh hasilnya',
    howStepSecure: 'Cepat, aman, dan mudah',
    homeTrustFooter: 'PDF911 bekerja offline di browser. File tidak diunggah. Privasi Anda yang utama.',
    faq: 'Pertanyaan umum',
    commonTasks: 'Tugas umum',
    popularSearches: 'Pencarian populer',
    morePdfTools: 'Alat PDF lainnya',
    morePdfToolsLead: 'Buka alat terkait untuk halaman, kompresi, konversi, dan pengeditan.',
    allTools: 'Semua alat',
    current: 'aktif',
    openingTool: 'Membuka alat...',
    loadToolError: 'Alat tidak dapat dimuat',
    checkConnection: 'Periksa koneksi dan coba buka alat lagi.',
    retry: 'Coba lagi',
    openingSettings: 'Membuka pengaturan...',
    settingsLoadError: 'Pengaturan tidak dapat dimuat:',
    pageLoadError: 'Halaman tidak dapat dimuat:',
    categories: { pages: 'Halaman', optimize: 'Optimalkan', convert: 'Konversi', edit: 'Edit', security: 'Keamanan' },
    install: {
      install: 'Instal',
      homeScreen: 'Layar utama',
      save: 'Simpan',
      installHint: `Instal ${BRAND_NAME} sebagai aplikasi`,
      iosHint: `Tambahkan ${BRAND_NAME} ke layar utama`,
      saveHint: `Simpan ${BRAND_NAME} di perangkat ini`,
      iosTitle: `Tambahkan ${BRAND_NAME} ke layar utama`,
      androidTitle: `Instal ${BRAND_NAME} di Android`,
      desktopTitle: `Simpan ${BRAND_NAME} di komputer ini`,
    },
  },
  ja: {
    languageLabel: '言語',
    footer: `${BRAND_NAME} — ブラウザでPDFを結合、圧縮、分割、編集、変換できる無料ツールです。`,
    privacyLink: 'プライバシー',
    dataLink: 'データ',
    close: '閉じる',
    analyticsTitle: '分析Cookie',
    analyticsLead: `${BRAND_NAME} はプライバシーに配慮した分析を使用します。ファイルはアップロードされません。`,
    details: '詳細',
    accept: '許可',
    switchToLightTheme: 'ライトテーマに切り替え',
    switchToDarkTheme: 'ダークテーマに切り替え',
    lightTheme: 'ライトテーマ',
    darkTheme: 'ダークテーマ',
    homeHeadline: 'PDFをオンラインで結合、圧縮、変換',
    heroLead: 'ブラウザで動く高速PDFツール。アップロード不要。ファイルは安全に端末内に残ります。',
    privateTitle: 'プライバシー重視',
    privateText: 'ファイルはブラウザ内で処理され、サーバーへアップロードされません。アカウントも不要です。',
    privacyChip: 'ファイルは端末内に残ります。プライベートで安全です。',
    pdfTools: 'PDFツール',
    toolFilterLabel: 'ツールフィルター',
    homeToolsLead: 'PDF911でページ整理、文書結合、PDF圧縮、ファイル変換、簡単な編集をサーバー送信なしで行えます。',
    dropFile: 'ここにファイルをドロップ、または選択',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'ファイルを選択',
    selectedFile: '選択したファイル:',
    countingPages: 'ページ数を確認中...',
    pageCountUnavailable: 'ページ数を取得できません',
    pageSingular: 'ページ',
    pagePlural: 'ページ',
    replaceFile: 'ファイルを変更',
    removeFile: '削除',
    choosePdfTool: '下のPDFツールを選択してください。選択したファイルがそのツールで開きます。',
    all: 'すべて',
    popular: '人気',
    compressFilter: '圧縮',
    recommendedPdfTools: 'おすすめPDFツール',
    detectedPdf: 'PDFを検出しました。何をしますか？',
    howItWorks: '使い方',
    howStepChoose: 'ファイルを選択またはドロップ',
    howStepProcess: 'ブラウザで処理',
    howStepDownload: '結果ファイルをダウンロード',
    howStepSecure: '高速、安全、かんたん',
    homeTrustFooter: 'PDF911はブラウザでオフライン動作します。ファイルはアップロードされません。プライバシーを優先します。',
    faq: 'よくある質問',
    commonTasks: 'よく使う作業',
    popularSearches: '人気の検索',
    morePdfTools: 'その他のPDFツール',
    morePdfToolsLead: 'ページ、圧縮、変換、編集の関連ツールへ移動できます。',
    allTools: 'すべてのツール',
    current: '現在',
    openingTool: 'ツールを開いています...',
    loadToolError: 'ツールを読み込めませんでした',
    checkConnection: '接続を確認して、もう一度ツールを開いてください。',
    retry: '再試行',
    openingSettings: '設定を開いています...',
    settingsLoadError: '設定を読み込めませんでした:',
    pageLoadError: 'ページを読み込めませんでした:',
    categories: { pages: 'ページ', optimize: '最適化', convert: '変換', edit: '編集', security: 'セキュリティ' },
    install: {
      install: 'インストール',
      homeScreen: 'ホーム画面',
      save: 'アプリをインストール',
      installHint: `${BRAND_NAME} をアプリとしてインストール`,
      iosHint: `${BRAND_NAME} をホーム画面に追加`,
      saveHint: `${BRAND_NAME} をこの端末に保存`,
      iosTitle: `${BRAND_NAME} をホーム画面に追加`,
      iosLead: 'iOSではサイトが自動で追加されません。Safariからアイコンを追加してください。',
      iosSteps: ['Safariでサイトを開きます。', '共有をタップします。', 'ホーム画面に追加を選んで確認します。'],
      androidTitle: `${BRAND_NAME} をAndroidにインストール`,
      androidLead: 'インストール案内が表示されない場合は、Chromeメニューからサイトを追加してください。',
      androidSteps: ['ブラウザメニューを開きます。', 'アプリをインストールまたはホーム画面に追加をタップします。', 'アイコンを確認します。'],
      desktopTitle: `${BRAND_NAME} をこのコンピューターに保存`,
      desktopLead: 'ブラウザはサイトを自動でブックマークに追加できません。',
      desktopSteps: (shortcut) => [`${shortcut} を押します。`, 'すぐ使う場合はブックマークバーを選びます。', 'ChromeやEdgeではアドレスバーまたはメニューからアプリとしてインストールすることもできます。'],
    },
  },
  vi: {
    languageLabel: 'Ngôn ngữ',
    footer: `${BRAND_NAME} — công cụ PDF miễn phí để gộp, nén, tách, chỉnh sửa và chuyển đổi tệp trong trình duyệt.`,
    privacyLink: 'Quyền riêng tư',
    dataLink: 'Dữ liệu',
    close: 'Đóng',
    analyticsTitle: 'Cookie phân tích',
    analyticsLead: `${BRAND_NAME} dùng phân tích chú trọng quyền riêng tư. Tệp không được tải lên.`,
    details: 'Chi tiết',
    accept: 'Chấp nhận',
    switchToLightTheme: 'Chuyển sang giao diện sáng',
    switchToDarkTheme: 'Chuyển sang giao diện tối',
    lightTheme: 'Giao diện sáng',
    darkTheme: 'Giao diện tối',
    homeHeadline: 'Gộp, nén và chuyển đổi PDF online',
    heroLead: 'Công cụ PDF nhanh chạy ngay trong trình duyệt. Không tải lên. Tệp của bạn luôn riêng tư và an toàn.',
    privateTitle: 'Riêng tư từ thiết kế',
    privateText: 'Tệp được xử lý trong trình duyệt và không được tải lên máy chủ của chúng tôi. Không cần tài khoản.',
    privacyChip: 'Tệp ở lại trên thiết bị của bạn. Riêng tư và an toàn.',
    pdfTools: 'Công cụ PDF',
    toolFilterLabel: 'Bộ lọc công cụ',
    homeToolsLead: 'Dùng PDF911 để sắp xếp trang, gộp tài liệu, nén PDF, chuyển đổi tệp và chỉnh sửa nhanh mà không gửi tài liệu lên máy chủ.',
    dropFile: 'Thả tệp vào đây hoặc chọn tệp',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'Chọn tệp',
    selectedFile: 'Tệp đã chọn:',
    countingPages: 'đang đếm trang...',
    pageCountUnavailable: 'không lấy được số trang',
    pageSingular: 'trang',
    pagePlural: 'trang',
    replaceFile: 'Thay tệp',
    removeFile: 'Xóa',
    choosePdfTool: 'Chọn một công cụ PDF bên dưới. Tệp đã chọn sẽ mở trong công cụ đó.',
    all: 'Tất cả',
    popular: 'Phổ biến',
    compressFilter: 'Nén',
    recommendedPdfTools: 'Công cụ PDF đề xuất',
    detectedPdf: 'Chúng tôi phát hiện một PDF. Bạn muốn làm gì?',
    howItWorks: 'Cách hoạt động',
    howStepChoose: 'Chọn hoặc thả tệp',
    howStepProcess: 'Xử lý trong trình duyệt',
    howStepDownload: 'Tải xuống kết quả',
    howStepSecure: 'Nhanh, an toàn và dễ dùng',
    homeTrustFooter: 'PDF911 hoạt động ngoại tuyến trong trình duyệt. Tệp không được tải lên. Quyền riêng tư là ưu tiên.',
    faq: 'Câu hỏi thường gặp',
    commonTasks: 'Tác vụ thường dùng',
    popularSearches: 'Tìm kiếm phổ biến',
    morePdfTools: 'Thêm công cụ PDF',
    morePdfToolsLead: 'Chuyển đến các công cụ liên quan cho trang, nén, chuyển đổi và chỉnh sửa.',
    allTools: 'Tất cả công cụ',
    current: 'hiện tại',
    openingTool: 'Đang mở công cụ...',
    loadToolError: 'Không thể tải công cụ',
    checkConnection: 'Kiểm tra kết nối và thử mở lại công cụ.',
    retry: 'Thử lại',
    openingSettings: 'Đang mở cài đặt...',
    settingsLoadError: 'Không thể tải cài đặt:',
    pageLoadError: 'Không thể tải trang:',
    categories: { pages: 'Trang', optimize: 'Tối ưu', convert: 'Chuyển đổi', edit: 'Chỉnh sửa', security: 'Bảo mật' },
    install: {
      install: 'Cài đặt',
      homeScreen: 'Màn hình chính',
      save: 'Cài ứng dụng',
      installHint: `Cài ${BRAND_NAME} như ứng dụng`,
      iosHint: `Thêm ${BRAND_NAME} vào màn hình chính`,
      saveHint: `Lưu ${BRAND_NAME} trên thiết bị này`,
      iosTitle: `Thêm ${BRAND_NAME} vào màn hình chính`,
      iosLead: 'iOS không cho phép website tự thêm biểu tượng. Hãy thêm từ Safari.',
      iosSteps: ['Mở trang trong Safari.', 'Nhấn Chia sẻ.', 'Chọn Thêm vào màn hình chính và xác nhận.'],
      androidTitle: `Cài ${BRAND_NAME} trên Android`,
      androidLead: 'Nếu trình duyệt không hiển thị lời nhắc cài đặt, hãy thêm trang từ menu Chrome.',
      androidSteps: ['Mở menu trình duyệt.', 'Nhấn Cài ứng dụng hoặc Thêm vào màn hình chính.', 'Xác nhận biểu tượng.'],
      desktopTitle: `Lưu ${BRAND_NAME} trên máy tính này`,
      desktopLead: 'Trình duyệt không cho phép website tự thêm vào dấu trang.',
      desktopSteps: (shortcut) => [`Nhấn ${shortcut}.`, 'Chọn thanh dấu trang nếu muốn truy cập nhanh.', 'Trong Chrome hoặc Edge, bạn cũng có thể cài trang như ứng dụng từ thanh địa chỉ hoặc menu trình duyệt.'],
    },
  },
  ko: {
    languageLabel: '언어',
    footer: `${BRAND_NAME} — 브라우저에서 PDF 병합, 압축, 분할, 편집, 변환을 처리하는 무료 도구입니다.`,
    privacyLink: '개인정보',
    dataLink: '데이터',
    close: '닫기',
    analyticsTitle: '분석 쿠키',
    analyticsLead: `${BRAND_NAME}은 개인정보를 고려한 분석을 사용합니다. 파일은 업로드되지 않습니다.`,
    details: '자세히',
    accept: '허용',
    switchToLightTheme: '라이트 테마로 전환',
    switchToDarkTheme: '다크 테마로 전환',
    lightTheme: '라이트 테마',
    darkTheme: '다크 테마',
    homeHeadline: 'PDF 병합, 압축, 변환을 온라인으로',
    heroLead: '브라우저에서 바로 작동하는 빠른 PDF 도구입니다. 업로드 없이 파일을 안전하게 보관합니다.',
    privateTitle: '개인정보 중심 설계',
    privateText: '파일은 브라우저에서 처리되며 서버로 업로드되지 않습니다. 계정도 필요하지 않습니다.',
    privacyChip: '파일은 기기에 남습니다. 비공개이며 안전합니다.',
    pdfTools: 'PDF 도구',
    toolFilterLabel: '도구 필터',
    homeToolsLead: 'PDF911로 페이지 정리, 문서 병합, PDF 압축, 파일 변환, 빠른 편집을 서버 전송 없이 처리하세요.',
    dropFile: '여기에 파일을 놓거나 파일 선택',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: '파일 선택',
    selectedFile: '선택한 파일:',
    countingPages: '페이지 수 확인 중...',
    pageCountUnavailable: '페이지 수를 확인할 수 없음',
    pageSingular: '페이지',
    pagePlural: '페이지',
    replaceFile: '파일 바꾸기',
    removeFile: '삭제',
    choosePdfTool: '아래에서 PDF 도구를 선택하세요. 선택한 파일이 해당 도구에서 열립니다.',
    all: '전체',
    popular: '인기',
    compressFilter: '압축',
    recommendedPdfTools: '추천 PDF 도구',
    detectedPdf: 'PDF를 감지했습니다. 무엇을 하시겠습니까?',
    howItWorks: '작동 방식',
    howStepChoose: '파일 선택 또는 놓기',
    howStepProcess: '브라우저에서 처리',
    howStepDownload: '결과 파일 다운로드',
    howStepSecure: '빠르고 안전하며 쉽습니다',
    homeTrustFooter: 'PDF911은 브라우저에서 오프라인으로 작동합니다. 파일은 업로드되지 않습니다. 개인정보를 우선합니다.',
    faq: '자주 묻는 질문',
    commonTasks: '일반 작업',
    popularSearches: '인기 검색',
    morePdfTools: '더 많은 PDF 도구',
    morePdfToolsLead: '페이지, 압축, 변환, 편집 관련 도구로 이동하세요.',
    allTools: '모든 도구',
    current: '현재',
    openingTool: '도구 여는 중...',
    loadToolError: '도구를 불러올 수 없습니다',
    checkConnection: '연결을 확인하고 도구를 다시 열어보세요.',
    retry: '다시 시도',
    openingSettings: '설정 여는 중...',
    settingsLoadError: '설정을 불러올 수 없습니다:',
    pageLoadError: '페이지를 불러올 수 없습니다:',
    categories: { pages: '페이지', optimize: '최적화', convert: '변환', edit: '편집', security: '보안' },
    install: {
      install: '설치',
      homeScreen: '홈 화면',
      save: '앱 설치',
      installHint: `${BRAND_NAME}을 앱으로 설치`,
      iosHint: `${BRAND_NAME}을 홈 화면에 추가`,
      saveHint: `${BRAND_NAME}을 이 기기에 저장`,
      iosTitle: `${BRAND_NAME}을 홈 화면에 추가`,
      iosLead: 'iOS에서는 웹사이트가 자동으로 추가될 수 없습니다. Safari에서 아이콘을 추가하세요.',
      iosSteps: ['Safari에서 사이트를 엽니다.', '공유를 탭합니다.', '홈 화면에 추가를 선택하고 확인합니다.'],
      androidTitle: `Android에 ${BRAND_NAME} 설치`,
      androidLead: '브라우저가 설치 안내를 표시하지 않으면 Chrome 메뉴에서 사이트를 추가하세요.',
      androidSteps: ['브라우저 메뉴를 엽니다.', '앱 설치 또는 홈 화면에 추가를 탭합니다.', '아이콘을 확인합니다.'],
      desktopTitle: `이 컴퓨터에 ${BRAND_NAME} 저장`,
      desktopLead: '브라우저는 웹사이트가 자동으로 북마크에 추가되는 것을 허용하지 않습니다.',
      desktopSteps: (shortcut) => [`${shortcut}을 누릅니다.`, '빠른 접근이 필요하면 북마크 바를 선택합니다.', 'Chrome 또는 Edge에서는 주소 표시줄이나 브라우저 메뉴에서 앱으로 설치할 수도 있습니다.'],
    },
  },
  ar: {
    languageLabel: 'اللغة',
    footer: `${BRAND_NAME} — أدوات PDF مجانية للدمج والضغط والتقسيم والتحرير والتحويل داخل المتصفح.`,
    privacyLink: 'الخصوصية',
    dataLink: 'البيانات',
    close: 'إغلاق',
    analyticsTitle: 'ملفات تعريف الارتباط للتحليلات',
    analyticsLead: `يستخدم ${BRAND_NAME} تحليلات تراعي الخصوصية. لا يتم رفع الملفات.`,
    details: 'التفاصيل',
    accept: 'قبول',
    switchToLightTheme: 'التبديل إلى الوضع الفاتح',
    switchToDarkTheme: 'التبديل إلى الوضع الداكن',
    lightTheme: 'الوضع الفاتح',
    darkTheme: 'الوضع الداكن',
    homeHeadline: 'ادمج واضغط وحول PDF عبر الإنترنت',
    heroLead: 'أدوات PDF سريعة تعمل داخل المتصفح. بدون رفع ملفات. تبقى ملفاتك خاصة وآمنة.',
    privateTitle: 'مصمم للخصوصية',
    privateText: 'تتم معالجة الملفات في متصفحك ولا يتم رفعها إلى خادمنا. لا حاجة إلى حساب.',
    privacyChip: 'تبقى الملفات على جهازك. خاص وآمن.',
    pdfTools: 'أدوات PDF',
    toolFilterLabel: 'تصفية الأدوات',
    homeToolsLead: 'استخدم PDF911 لتنظيم الصفحات ودمج المستندات وضغط PDF وتحويل الملفات وإجراء تعديلات سريعة دون إرسال المستندات إلى خادم.',
    dropFile: 'أفلت الملفات هنا أو اختر الملفات',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'اختر الملفات',
    selectedFile: 'الملف المحدد:',
    countingPages: 'جار عد الصفحات...',
    pageCountUnavailable: 'عدد الصفحات غير متاح',
    pageSingular: 'صفحة',
    pagePlural: 'صفحات',
    replaceFile: 'استبدال الملف',
    removeFile: 'إزالة',
    choosePdfTool: 'اختر أداة PDF أدناه. سيتم فتح الملف المحدد داخل تلك الأداة.',
    all: 'الكل',
    popular: 'شائع',
    compressFilter: 'ضغط',
    recommendedPdfTools: 'أدوات PDF موصى بها',
    detectedPdf: 'اكتشفنا ملف PDF. ماذا تريد أن تفعل؟',
    howItWorks: 'طريقة العمل',
    howStepChoose: 'اختر أو أفلت ملفك',
    howStepProcess: 'نعالجه في المتصفح',
    howStepDownload: 'نزل ملف النتيجة',
    howStepSecure: 'سريع وآمن وسهل',
    homeTrustFooter: 'يعمل PDF911 دون اتصال داخل المتصفح. لا يتم رفع الملفات. خصوصيتك أولا.',
    faq: 'الأسئلة الشائعة',
    commonTasks: 'مهام شائعة',
    popularSearches: 'عمليات بحث شائعة',
    morePdfTools: 'المزيد من أدوات PDF',
    morePdfToolsLead: 'انتقل إلى أدوات مرتبطة للصفحات والضغط والتحويل والتحرير.',
    allTools: 'كل الأدوات',
    current: 'الحالي',
    openingTool: 'جار فتح الأداة...',
    loadToolError: 'تعذر تحميل الأداة',
    checkConnection: 'تحقق من الاتصال وحاول فتح الأداة مرة أخرى.',
    retry: 'إعادة المحاولة',
    openingSettings: 'جار فتح الإعدادات...',
    settingsLoadError: 'تعذر تحميل الإعدادات:',
    pageLoadError: 'تعذر تحميل الصفحة:',
    categories: { pages: 'الصفحات', optimize: 'تحسين', convert: 'تحويل', edit: 'تحرير', security: 'الأمان' },
    install: {
      install: 'تثبيت',
      homeScreen: 'الشاشة الرئيسية',
      save: 'تثبيت التطبيق',
      installHint: `تثبيت ${BRAND_NAME} كتطبيق`,
      iosHint: `إضافة ${BRAND_NAME} إلى الشاشة الرئيسية`,
      saveHint: `حفظ ${BRAND_NAME} على هذا الجهاز`,
      iosTitle: `إضافة ${BRAND_NAME} إلى الشاشة الرئيسية`,
      iosLead: 'لا يسمح iOS للمواقع بإضافة نفسها تلقائيا. أضف الأيقونة من Safari.',
      iosSteps: ['افتح الموقع في Safari.', 'اضغط مشاركة.', 'اختر إضافة إلى الشاشة الرئيسية وأكد.'],
      androidTitle: `تثبيت ${BRAND_NAME} على Android`,
      androidLead: 'إذا لم يظهر المتصفح طلب التثبيت، أضف الموقع من قائمة Chrome.',
      androidSteps: ['افتح قائمة المتصفح.', 'اضغط تثبيت التطبيق أو إضافة إلى الشاشة الرئيسية.', 'أكد الأيقونة.'],
      desktopTitle: `حفظ ${BRAND_NAME} على هذا الكمبيوتر`,
      desktopLead: 'لا تسمح المتصفحات للمواقع بإضافة نفسها إلى الإشارات المرجعية تلقائيا.',
      desktopSteps: (shortcut) => [`اضغط ${shortcut}.`, 'اختر شريط الإشارات إذا كنت تريد وصولا سريعا.', 'في Chrome أو Edge يمكنك أيضا تثبيت الموقع كتطبيق من شريط العنوان أو قائمة المتصفح.'],
    },
  },
  hi: {
    languageLabel: 'भाषा',
    footer: `${BRAND_NAME} — ब्राउज़र में PDF मिलाने, कंप्रेस करने, बांटने, एडिट करने और कन्वर्ट करने के मुफ्त टूल।`,
    privacyLink: 'प्राइवेसी',
    dataLink: 'डेटा',
    close: 'बंद करें',
    analyticsTitle: 'Analytics cookies',
    analyticsLead: `${BRAND_NAME} privacy-conscious analytics इस्तेमाल करता है। फाइलें अपलोड नहीं होतीं।`,
    details: 'विवरण',
    accept: 'स्वीकार करें',
    switchToLightTheme: 'लाइट थीम पर जाएं',
    switchToDarkTheme: 'डार्क थीम पर जाएं',
    lightTheme: 'लाइट थीम',
    darkTheme: 'डार्क थीम',
    homeHeadline: 'PDF ऑनलाइन मिलाएं, कंप्रेस करें और कन्वर्ट करें',
    heroLead: 'तेज PDF टूल जो सीधे आपके ब्राउज़र में चलते हैं। कोई अपलोड नहीं। आपकी फाइलें निजी और सुरक्षित रहती हैं।',
    privateTitle: 'प्राइवेसी के लिए बनाया गया',
    privateText: 'फाइलें आपके ब्राउज़र में प्रोसेस होती हैं और हमारे सर्वर पर अपलोड नहीं होतीं। अकाउंट की जरूरत नहीं।',
    privacyChip: 'फाइलें आपके डिवाइस पर रहती हैं। निजी और सुरक्षित।',
    pdfTools: 'PDF टूल',
    toolFilterLabel: 'टूल फिल्टर',
    homeToolsLead: 'PDF911 से पेज व्यवस्थित करें, दस्तावेज़ मिलाएं, PDF कंप्रेस करें, फाइलें कन्वर्ट करें और दस्तावेज़ सर्वर पर भेजे बिना तेज एडिट करें।',
    dropFile: 'फाइलें यहां छोड़ें या फाइलें चुनें',
    fileTypes: 'PDF, Word, Excel, JPG, PNG, CSV',
    chooseFile: 'फाइलें चुनें',
    selectedFile: 'चुनी गई फाइल:',
    countingPages: 'पेज गिने जा रहे हैं...',
    pageCountUnavailable: 'पेज संख्या उपलब्ध नहीं',
    pageSingular: 'पेज',
    pagePlural: 'पेज',
    replaceFile: 'फाइल बदलें',
    removeFile: 'हटाएं',
    choosePdfTool: 'नीचे PDF टूल चुनें। चुनी गई फाइल उसी टूल में खुलेगी।',
    all: 'सभी',
    popular: 'लोकप्रिय',
    compressFilter: 'कंप्रेस',
    recommendedPdfTools: 'सुझाए गए PDF टूल',
    detectedPdf: 'हमें PDF मिला। आप क्या करना चाहते हैं?',
    howItWorks: 'कैसे काम करता है',
    howStepChoose: 'फाइल चुनें या छोड़ें',
    howStepProcess: 'हम इसे ब्राउज़र में प्रोसेस करते हैं',
    howStepDownload: 'अपनी result file डाउनलोड करें',
    howStepSecure: 'तेज, सुरक्षित और आसान',
    homeTrustFooter: 'PDF911 ब्राउज़र में offline काम करता है। फाइलें अपलोड नहीं होतीं। आपकी privacy पहले है।',
    faq: 'अक्सर पूछे जाने वाले सवाल',
    commonTasks: 'आम काम',
    popularSearches: 'लोकप्रिय खोजें',
    morePdfTools: 'और PDF टूल',
    morePdfToolsLead: 'पेज, compression, conversion और editing से जुड़े टूल खोलें।',
    allTools: 'सभी टूल',
    current: 'वर्तमान',
    openingTool: 'टूल खुल रहा है...',
    loadToolError: 'टूल लोड नहीं हो सका',
    checkConnection: 'कनेक्शन जांचें और टूल फिर से खोलें।',
    retry: 'फिर कोशिश करें',
    openingSettings: 'सेटिंग खुल रही हैं...',
    settingsLoadError: 'सेटिंग लोड नहीं हो सकीं:',
    pageLoadError: 'पेज लोड नहीं हो सका:',
    categories: { pages: 'पेज', optimize: 'ऑप्टिमाइज़', convert: 'कन्वर्ट', edit: 'एडिट', security: 'सुरक्षा' },
    install: {
      install: 'इंस्टॉल',
      homeScreen: 'होम स्क्रीन',
      save: 'ऐप इंस्टॉल करें',
      installHint: `${BRAND_NAME} को ऐप की तरह इंस्टॉल करें`,
      iosHint: `${BRAND_NAME} को होम स्क्रीन पर जोड़ें`,
      saveHint: `${BRAND_NAME} को इस डिवाइस पर सेव करें`,
      iosTitle: `${BRAND_NAME} को होम स्क्रीन पर जोड़ें`,
      iosLead: 'iOS वेबसाइटों को अपने आप ऐसा करने नहीं देता। Safari से icon जोड़ें।',
      iosSteps: ['Safari में साइट खोलें।', 'Share पर टैप करें।', 'Add to Home Screen चुनें और confirm करें।'],
      androidTitle: `Android पर ${BRAND_NAME} इंस्टॉल करें`,
      androidLead: 'अगर browser ने install prompt नहीं दिखाया, तो Chrome menu से site जोड़ें।',
      androidSteps: ['Browser menu खोलें।', 'Install app या Add to Home screen पर टैप करें।', 'Icon confirm करें।'],
      desktopTitle: `${BRAND_NAME} को इस कंप्यूटर पर सेव करें`,
      desktopLead: 'Browsers वेबसाइटों को अपने आप bookmarks में जोड़ने नहीं देते।',
      desktopSteps: (shortcut) => [`${shortcut} दबाएं।`, 'जल्दी access चाहिए तो bookmarks bar चुनें।', 'Chrome या Edge में आप address bar या browser menu से site को app की तरह install भी कर सकते हैं।'],
    },
  },
};

const INSTALL_COPY_COMPLETIONS: Record<string, Partial<InstallCopy>> = {
  ru: {
    iosLead: 'iOS не позволяет сайтам делать это автоматически. Добавьте значок через Safari.',
    iosSteps: ['Откройте сайт в Safari.', 'Нажмите «Поделиться».', 'Выберите «На экран Домой» и подтвердите.'],
    androidLead: 'Если браузер не показал установку, добавьте сайт через меню Chrome.',
    androidSteps: ['Откройте меню браузера.', 'Нажмите «Установить приложение» или «Добавить на главный экран».', 'Подтвердите значок.'],
    desktopLead: 'Браузеры не позволяют сайтам автоматически добавлять себя в закладки.',
    desktopSteps: (shortcut) => [`Нажмите ${shortcut}.`, 'Выберите панель закладок, если нужен быстрый доступ.', 'В Chrome или Edge сайт также можно установить как приложение из адресной строки или меню браузера.'],
  },
  'zh-CN': {
    iosLead: 'iOS 不允许网站自动完成此操作。请通过 Safari 添加图标。',
    iosSteps: ['在 Safari 中打开网站。', '点击“分享”。', '选择“添加到主屏幕”并确认。'],
    androidLead: '如果浏览器没有显示安装提示，请从 Chrome 菜单添加网站。',
    androidSteps: ['打开浏览器菜单。', '点击“安装应用”或“添加到主屏幕”。', '确认图标。'],
    desktopLead: '浏览器不允许网站自动把自己加入书签。',
    desktopSteps: (shortcut) => [`按 ${shortcut}。`, '如果想快速访问，请选择书签栏。', '在 Chrome 或 Edge 中，也可以从地址栏或浏览器菜单将网站安装为应用。'],
  },
  'pt-BR': {
    iosLead: 'O iOS nao permite que sites facam isso automaticamente. Adicione o icone pelo Safari.',
    iosSteps: ['Abra o site no Safari.', 'Toque em Compartilhar.', 'Escolha Adicionar a Tela de Inicio e confirme.'],
    androidLead: 'Se o navegador nao mostrou o aviso de instalacao, adicione o site pelo menu do Chrome.',
    androidSteps: ['Abra o menu do navegador.', 'Toque em Instalar app ou Adicionar a tela inicial.', 'Confirme o icone.'],
    desktopLead: 'Os navegadores nao permitem que sites se adicionem aos favoritos automaticamente.',
    desktopSteps: (shortcut) => [`Pressione ${shortcut}.`, 'Escolha a barra de favoritos se quiser acesso rapido.', 'No Chrome ou Edge, voce tambem pode instalar o site como app pela barra de enderecos ou pelo menu do navegador.'],
  },
  de: {
    iosLead: 'iOS erlaubt Websites nicht, dies automatisch zu tun. Fugen Sie das Symbol uber Safari hinzu.',
    iosSteps: ['Offnen Sie die Website in Safari.', 'Tippen Sie auf Teilen.', 'Wahlen Sie Zum Home-Bildschirm und bestatigen Sie.'],
    androidLead: 'Wenn der Browser keine Installationsaufforderung angezeigt hat, fugen Sie die Website uber das Chrome-Menu hinzu.',
    androidSteps: ['Offnen Sie das Browsermenu.', 'Tippen Sie auf App installieren oder Zum Startbildschirm hinzufugen.', 'Bestatigen Sie das Symbol.'],
    desktopLead: 'Browser erlauben Websites nicht, sich automatisch zu Lesezeichen hinzuzufugen.',
    desktopSteps: (shortcut) => [`Drucken Sie ${shortcut}.`, 'Wahlen Sie die Lesezeichenleiste, wenn Sie schnellen Zugriff mochten.', 'In Chrome oder Edge konnen Sie die Website auch uber die Adressleiste oder das Browsermenu als App installieren.'],
  },
  fr: {
    iosLead: 'iOS ne permet pas aux sites de le faire automatiquement. Ajoutez l icone depuis Safari.',
    iosSteps: ['Ouvrez le site dans Safari.', 'Touchez Partager.', 'Choisissez Ajouter a l ecran d accueil et confirmez.'],
    androidLead: 'Si le navigateur n a pas affiche l invite d installation, ajoutez le site depuis le menu Chrome.',
    androidSteps: ['Ouvrez le menu du navigateur.', 'Touchez Installer l application ou Ajouter a l ecran d accueil.', 'Confirmez l icone.'],
    desktopLead: 'Les navigateurs ne permettent pas aux sites de s ajouter automatiquement aux favoris.',
    desktopSteps: (shortcut) => [`Appuyez sur ${shortcut}.`, 'Choisissez la barre de favoris si vous voulez un acces rapide.', 'Dans Chrome ou Edge, vous pouvez aussi installer le site comme application depuis la barre d adresse ou le menu du navigateur.'],
  },
  it: {
    iosLead: 'iOS non consente ai siti di farlo automaticamente. Aggiungi l icona da Safari.',
    iosSteps: ['Apri il sito in Safari.', 'Tocca Condividi.', 'Scegli Aggiungi alla schermata Home e conferma.'],
    androidLead: 'Se il browser non ha mostrato il prompt di installazione, aggiungi il sito dal menu di Chrome.',
    androidSteps: ['Apri il menu del browser.', 'Tocca Installa app o Aggiungi alla schermata Home.', 'Conferma l icona.'],
    desktopLead: 'I browser non consentono ai siti di aggiungersi automaticamente ai segnalibri.',
    desktopSteps: (shortcut) => [`Premi ${shortcut}.`, 'Scegli la barra dei preferiti se vuoi accesso rapido.', 'In Chrome o Edge puoi anche installare il sito come app dalla barra degli indirizzi o dal menu del browser.'],
  },
  nl: {
    iosLead: 'iOS laat websites dit niet automatisch doen. Voeg het pictogram toe via Safari.',
    iosSteps: ['Open de site in Safari.', 'Tik op Delen.', 'Kies Zet op beginscherm en bevestig.'],
    androidLead: 'Als de browser geen installatievenster liet zien, voeg de site dan toe via het Chrome-menu.',
    androidSteps: ['Open het browsermenu.', 'Tik op App installeren of Toevoegen aan startscherm.', 'Bevestig het pictogram.'],
    desktopLead: 'Browsers laten websites zichzelf niet automatisch aan bladwijzers toevoegen.',
    desktopSteps: (shortcut) => [`Druk op ${shortcut}.`, 'Kies de bladwijzerbalk als u snel toegang wilt.', 'In Chrome of Edge kunt u de site ook als app installeren via de adresbalk of het browsermenu.'],
  },
  pl: {
    iosLead: 'iOS nie pozwala stronom robic tego automatycznie. Dodaj ikone przez Safari.',
    iosSteps: ['Otworz strone w Safari.', 'Stuknij Udostepnij.', 'Wybierz Dodaj do ekranu glownego i potwierdz.'],
    androidLead: 'Jesli przegladarka nie pokazala instalacji, dodaj strone z menu Chrome.',
    androidSteps: ['Otworz menu przegladarki.', 'Stuknij Zainstaluj aplikacje lub Dodaj do ekranu glownego.', 'Potwierdz ikone.'],
    desktopLead: 'Przegladarki nie pozwalaja stronom automatycznie dodawac sie do zakladek.',
    desktopSteps: (shortcut) => [`Nacisnij ${shortcut}.`, 'Wybierz pasek zakladek, jesli chcesz szybki dostep.', 'W Chrome lub Edge mozesz tez zainstalowac strone jako aplikacje z paska adresu albo menu przegladarki.'],
  },
  tr: {
    iosLead: 'iOS sitelerin bunu otomatik yapmasina izin vermez. Simgeyi Safari uzerinden ekleyin.',
    iosSteps: ['Siteyi Safari de acin.', 'Paylas a dokunun.', 'Ana Ekrana Ekle yi secin ve onaylayin.'],
    androidLead: 'Tarayici kurulum uyarisi gostermediyse siteyi Chrome menusunden ekleyin.',
    androidSteps: ['Tarayici menusunu acin.', 'Uygulamayi yukle veya Ana ekrana ekle ye dokunun.', 'Simgeyi onaylayin.'],
    desktopLead: 'Tarayicilar sitelerin kendilerini otomatik olarak yer imlerine eklemesine izin vermez.',
    desktopSteps: (shortcut) => [`${shortcut} tuslarina basin.`, 'Hizli erisim istiyorsaniz yer imleri cubugunu secin.', 'Chrome veya Edge de siteyi adres cubugundan ya da tarayici menusunden uygulama olarak da yukleyebilirsiniz.'],
  },
  id: {
    iosLead: 'iOS tidak mengizinkan situs melakukan ini secara otomatis. Tambahkan ikon lewat Safari.',
    iosSteps: ['Buka situs di Safari.', 'Ketuk Bagikan.', 'Pilih Tambahkan ke Layar Utama dan konfirmasi.'],
    androidLead: 'Jika browser tidak menampilkan prompt instalasi, tambahkan situs dari menu Chrome.',
    androidSteps: ['Buka menu browser.', 'Ketuk Instal aplikasi atau Tambahkan ke layar utama.', 'Konfirmasi ikon.'],
    desktopLead: 'Browser tidak mengizinkan situs menambahkan dirinya sendiri ke bookmark secara otomatis.',
    desktopSteps: (shortcut) => [`Tekan ${shortcut}.`, 'Pilih bilah bookmark jika ingin akses cepat.', 'Di Chrome atau Edge, Anda juga bisa menginstal situs sebagai aplikasi dari bilah alamat atau menu browser.'],
  },
};

export function uiCopy(lang = currentClientLanguage()): UiCopy {
  const override = UI_COPY_OVERRIDES[lang] ?? {};
  const installCompletion = INSTALL_COPY_COMPLETIONS[lang] ?? {};
  return {
    ...EN_UI_COPY,
    ...override,
    categories: {
      ...EN_UI_COPY.categories,
      ...(override.categories ?? {}),
    },
    install: {
      ...EN_UI_COPY.install,
      ...installCompletion,
      ...(override.install ?? {}),
    },
  };
}
